import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useExpertSupport } from '../../contexts/ExpertSupportContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaGraduationCap, FaSearch, FaCalendarAlt, FaStar, FaBook, FaQuestionCircle, FaPaperPlane, FaFileUpload, FaClock, FaUserGraduate, FaFlask, FaProjectDiagram, FaChalkboardTeacher, FaLaptopCode, FaFileAlt, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import SEO from '../SEO';
import './ExpertSupport.css';

const ExpertSupport = () => {
  const { 
    professors, 
    supportSessions, 
    loading, 
    error,
    createSupportSession,
    getProfessorAvailability,
    createSupportRequest,
    waitForExpert,
    initializeSession,
    initializeChat,
    sendMessage,
    uploadFile,
    removeAttachment
  } = useExpertSupport();
  
  const { currentUser } = useAuth();
  const { addNotification } = useNotifications();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfessor, setSelectedProfessor] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);

  const [supportRequest, setSupportRequest] = useState({
    subject: '',
    topic: '',
    question: '',
    supportType: 'general',
    urgency: 'medium',
    attachments: []
  });

  const [activeSession, setActiveSession] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [expert, setExpert] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, waiting, active, completed
  const [selectedExpertiseFilter, setSelectedExpertiseFilter] = useState('all');

  // Filter out the professors to be removed
  const filteredProfessors = professors.filter(professor => 
    professor.name !== "Dr. Sarah Johnson" && 
    professor.name !== "Prof. Michael Chen"
  );

  const supportTypes = [
    { id: 'general', name: 'General Academic Support', icon: FaQuestionCircle },
    { id: 'research', name: 'Research Assistance', icon: FaFlask },
    { id: 'project', name: 'Project Help', icon: FaProjectDiagram },
    { id: 'tutoring', name: 'One-on-One Tutoring', icon: FaChalkboardTeacher },
    { id: 'technical', name: 'Technical Support', icon: FaLaptopCode },
    { id: 'writing', name: 'Academic Writing Help', icon: FaFileAlt }
  ];

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleProfessorSelect = async (professor) => {
    setSelectedProfessor(professor);
    try {
      const availability = await getProfessorAvailability(professor.id);
      setSelectedProfessor(prev => ({ ...prev, availability }));
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  };

  const handleBookSession = async (sessionData) => {
    try {
      await createSupportSession(sessionData);
      setShowBookingForm(false);
    } catch (err) {
      console.error('Error booking session:', err);
    }
  };

  const handleSubmitRequest = async () => {
    try {
      setStatus('loading');
      
      // Create the request in Firestore
      await addDoc(collection(db, 'expertRequests'), {
        ...supportRequest,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      // Send notification to the user
      addNotification({
        type: 'success',
        message: 'Your expert request has been submitted successfully. We will notify you once an expert is assigned.',
        link: '/expert-support'
      });
      
      // Also send notification to admins (in a real app, you'd have a list of admin IDs)
      // This is just an example - you'd need to implement admin notification based on your user structure
      addNotification({
        type: 'info',
        message: `New expert request submitted by ${currentUser.email}`,
        userId: 'adminUserId', // Replace with actual admin IDs
        link: '/admin/expert-requests'
      });
      
      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        // Clear form
        setSupportRequest({
          subject: '',
          topic: '',
          question: '',
          supportType: 'general',
          urgency: 'medium',
          attachments: []
        });
      }, 3000);
      
    } catch (error) {
      console.error('Error creating support request:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
      
      addNotification({
        type: 'error',
        message: 'Failed to submit expert request. Please try again.'
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSupportRequest(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendMessage = async (message) => {
    if (!activeSession) return;

    const newMessage = {
      id: Date.now(),
      sender: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };

    setChatHistory(prev => [...prev, newMessage]);
    await sendMessage(activeSession.id, newMessage);
  };

  const handleFileUpload = async (files) => {
    const uploadedFiles = await Promise.all(
      Array.from(files).map(file => uploadFile(file))
    );
    
    setSupportRequest(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...uploadedFiles]
    }));
  };

  const getSupportTypeLabel = () => {
    switch (supportRequest.supportType) {
      case 'research':
        return 'Research Question or Problem';
      case 'project':
        return 'Project Details';
      case 'tutoring':
        return 'Topics You Need Help With';
      case 'technical':
        return 'Technical Issue';
      case 'writing':
        return 'Writing Task Details';
      default:
        return 'Your Question';
    }
  };

  const getQuestionPlaceholder = () => {
    switch (supportRequest.supportType) {
      case 'research':
        return 'Describe your research question or problem in detail...';
      case 'project':
        return 'Explain your project requirements and where you need assistance...';
      case 'tutoring':
        return 'Describe the specific concepts you need help understanding...';
      case 'technical':
        return 'Describe the technical issue you are facing...';
      case 'writing':
        return 'Describe your paper, essay, or writing assignment...';
      default:
        return 'Describe your question in detail...';
    }
  };

  const filterProfessorsByExpertise = (professors) => {
    if (selectedExpertiseFilter === 'all') return professors;
    
    return professors.filter(professor => 
      professor.specialization.toLowerCase().includes(selectedExpertiseFilter.toLowerCase()) ||
      (professor.expertise && professor.expertise.includes(selectedExpertiseFilter))
    );
  };

  const displayProfessors = filterProfessorsByExpertise(
    filteredProfessors.filter(professor => 
      professor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      professor.specialization.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const renderRequestForm = () => (
    <div className="request-form">
      <h2>Get Expert Help</h2>
      
      <div className="support-types">
        {supportTypes.map(type => (
          <div 
            key={type.id}
            className={`support-type-card ${supportRequest.supportType === type.id ? 'selected' : ''}`}
            onClick={() => setSupportRequest(prev => ({ ...prev, supportType: type.id }))}
          >
            <type.icon className="support-type-icon" />
            <span>{type.name}</span>
          </div>
        ))}
      </div>

      <div className="form-group">
        <label>Subject</label>
        <select 
          name="subject"
          value={supportRequest.subject}
          onChange={handleInputChange}
        >
          <option value="">Select Service</option>
          <option value="research">Research</option>
          <option value="project">Project</option>
          <option value="tutoring">Tutoring</option>
          <option value="technical">Technical</option>
          <option value="computerscience">Computer Science</option>
          <option value="engineering">Engineering</option>
          <option value="literature">Literature</option>
          <option value="writing">Writing</option>
          <option value="business">Business</option>
          <option value="psychology">Psychology</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="form-group">
        <label>Topic</label>
        <input 
          type="text"
          name="topic"
          value={supportRequest.topic}
          onChange={handleInputChange}
          placeholder="Enter specific topic"
        />
      </div>

      <div className="form-group">
        <label>{getSupportTypeLabel()}</label>
        <textarea
          name="question"
          value={supportRequest.question}
          onChange={handleInputChange}
          placeholder={getQuestionPlaceholder()}
          rows={4}
        />
      </div>

      <div className="form-group">
        <label>Urgency</label>
        <select
          name="urgency"
          value={supportRequest.urgency}
          onChange={handleInputChange}
        >
          <option value="low">Low - Can wait</option>
          <option value="medium">Medium - Need help soon</option>
          <option value="high">High - Urgent help needed</option>
        </select>
      </div>

      <div className="form-group">
        <label>Attachments</label>
        <div className="file-upload">
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <FaFileUpload className="upload-icon" />
          <span>Upload files (optional)</span>
        </div>
        {supportRequest.attachments.length > 0 && (
          <div className="attachments-list">
            {supportRequest.attachments.map(file => (
              <div key={file.id} className="attachment-item">
                <span>{file.name}</span>
                <button onClick={() => removeAttachment(file.id)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button 
        className="submit-btn"
        onClick={handleSubmitRequest}
        disabled={status === 'loading' || !supportRequest.subject || !supportRequest.question}
      >
        {status === 'loading' ? 'Submitting...' : 'Request Expert Help'}
      </button>
    </div>
  );

  const renderChatSession = () => (
    <div className="chat-session">
      <div className="chat-header">
        <div className="expert-info">
          <FaUserGraduate className="expert-icon" />
          <div>
            <h3>{expert?.name}</h3>
            <p>{expert?.specialization}</p>
            {expert?.expertise && (
              <div className="expertise-tags">
                {expert.expertise.map(skill => (
                  <span key={skill} className="expertise-tag">{skill}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="session-info">
          <div className="session-type">
            {supportTypes.find(t => t.id === supportRequest.supportType)?.name || 'General Support'}
          </div>
          <div className="session-status">
            <span className={`status ${status}`}>{status}</span>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {chatHistory.length === 0 && (
          <div className="welcome-message">
            <h3>Session Started</h3>
            <p>Your expert will help you with your {supportRequest.supportType} request.</p>
            <div className="request-summary">
              <strong>Subject:</strong> {supportRequest.subject}<br />
              <strong>Topic:</strong> {supportRequest.topic}<br />
              <strong>{getSupportTypeLabel()}:</strong> {supportRequest.question}
            </div>
          </div>
        )}
        {chatHistory.map(message => (
          <div 
            key={message.id} 
            className={`message ${message.sender === 'user' ? 'user' : 'expert'}`}
          >
            <div className="message-content">
              {message.content}
            </div>
            <div className="message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}
      </div>

      <div className="chat-input">
        <input
          type="text"
          placeholder="Type your message..."
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage(e.target.value);
              e.target.value = '';
            }
          }}
        />
        <button onClick={() => {
          const input = document.querySelector('.chat-input input');
          handleSendMessage(input.value);
          input.value = '';
        }}>
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );

  const renderWaitingScreen = () => (
    <div className="waiting-screen">
      <FaClock className="waiting-icon" />
      <h2>Finding an Expert</h2>
      <p>Please wait while we match you with the best expert for your {supportRequest.supportType} request...</p>
      <div className="request-summary">
        <strong>Type:</strong> {supportTypes.find(t => t.id === supportRequest.supportType)?.name}<br />
        <strong>Subject:</strong> {supportRequest.subject}<br />
        <strong>Topic:</strong> {supportRequest.topic}
      </div>
      <div className="loading-spinner"></div>
    </div>
  );

  const renderSubmittedView = () => (
    <div className="submitted-screen">
      <FaCheck className="submitted-icon" />
      <h2>Request Submitted Successfully!</h2>
      <p>Thank you for your request. Our administrators will review your submission and connect you with an appropriate expert.</p>
      <div className="request-summary">
        <strong>Request Type:</strong> {supportTypes.find(t => t.id === supportRequest.supportType)?.name}<br />
        <strong>Subject:</strong> {supportRequest.subject}<br />
        <strong>Topic:</strong> {supportRequest.topic}<br />
        <strong>Urgency:</strong> {supportRequest.urgency === 'high' ? 'High - Urgent help needed' : 
                                  supportRequest.urgency === 'medium' ? 'Medium - Need help soon' : 
                                  'Low - Can wait'}
      </div>
      <p className="contact-note">You will receive a notification when an expert is assigned to your request. You can also check the status of your request in your profile.</p>
      <button 
        className="new-request-btn"
        onClick={() => setStatus('idle')}
      >
        Submit Another Request
      </button>
    </div>
  );
  
  const renderErrorView = () => (
    <div className="error-screen">
      <FaExclamationTriangle className="error-icon" />
      <h2>Request Submission Failed</h2>
      <p>There was an error submitting your request. Please try again or contact support if the problem persists.</p>
      <button 
        className="retry-btn"
        onClick={() => setStatus('idle')}
      >
        Try Again
      </button>
    </div>
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <>
      <SEO 
        title="Expert Support - Get Help from Professors"
        description="Connect with expert professors for academic support and guidance"
      />
      <div className="expert-support">
        <div className="header">
          <h1>Expert Support</h1>
          <p>Connect with university professors for academic guidance, research, projects, and tutoring</p>
        </div>

        <div className="support-content">
          {status === 'idle' && renderRequestForm()}
          {status === 'waiting' && renderWaitingScreen()}
          {status === 'active' && renderChatSession()}
          {status === 'submitted' && renderSubmittedView()}
          {status === 'error' && renderErrorView()}
        </div>
      </div>
    </>
  );
};

export default ExpertSupport; 